/* Verbatim port of the asset-detail drawer from service/review/index.html:
 *   - FIELD_LABELS, humanField, evidenceTier (~3101-3124) — small pure
 *     helpers F1 didn't extract (they're presentational, not "canonical
 *     computations"); co-located here since the drawer is their only
 *     caller in this task. renderAssetRows (§Appendix, F2c) also calls
 *     humanField/evidenceTier (~3210) — that port should import them from
 *     here rather than re-porting.
 *   - openDrawer/closeDrawer (~3262-3320) and the two document-level
 *     listeners that close it (~3321-3332).
 *
 * PARAMETERIZED (rule a): every function reads/writes the global `STATE`
 * (STATE.assets, STATE.conflicts, STATE.edges, STATE.selected) — each now
 * takes `state: AppPayloadState` as an explicit first parameter.
 * `STATE.selected = id` / `= null` become direct mutations of the same
 * `state` object the caller holds (payloadStore's value is one long-lived
 * object across a session, exactly like STATE was) — no reactive
 * notification for a selection change, same as the original (selection was
 * never itself a render trigger; only styleGraphSelection() reads it).
 *
 * DEVIATION: the original's two `document.addEventListener(...)` calls
 * (~3321, ~3332) ran once at script-eval time and closed over the global
 * `closeDrawer`/`STATE`. Module top-level code can't do that here (no
 * global STATE to close over), so they're wrapped in
 * `initDrawerGlobalListeners(getState)`, which main.ts's boot() calls once
 * with `() => payloadStore.get()`. Listener bodies are otherwise verbatim.
 *
 * DEVIATION (guard): styleGraphSelection() (~2880, the §4 graph's focus
 * mode) is F2c/F3 territory — ui/graph.ts's own module (F3b lands the real
 * implementation; this file only calls it, unchanged since F2c).
 *
 * WS hook (F2d): the original reassigned the global `openDrawer` AFTER
 * defining it (`const _wsOrigOpenDrawer = openDrawer; openDrawer =
 * function(id){ _wsOrigOpenDrawer(id); const a = STATE.assets.find(...);
 * if (a) WS.renderDrawerExtras(a); };`, ~4570-4575) so every drawer open
 * also appends the AM-workspace block. openDrawer() below calls
 * `WS.renderDrawerExtras(a)` directly at the end of its own body instead —
 * same net effect, without the reassignment trick ESM exports don't
 * support. `WS` is referenced as a bare global (not imported) — same
 * treatment as views/exec.ts/views/conflicts.ts (state/workspace.ts's
 * `declare global` block); renderDrawerExtras' own `if (this.available
 * !== true) return;` guard (kept intact) makes this call a no-op while the
 * shared board is offline, matching the original exactly.
 *
 * F3 (wave B): innerHTML → lit render(). `chip()` → `chipT()`. The
 * conditional-conditional `conns` list (`.map().join('<br>')`, a non-empty
 * separator) uses lit's `join()` directive. The pageFacts IIFE (`v +=
 * '<br>...'` string-building) rebuilds as native TemplateResult fragments
 * assembled by adjacent bindings inside one outer `html` template, instead
 * of one progressively-concatenated string — same avoid-unsafeHTML call
 * F3a made for entity.ts's kpWord/chainClause. `fieldRows` (`.map().join(
 * '')`) becomes a plain array of `<tr>` TemplateResults, matching
 * convention #6.
 */
import { html, nothing, render } from 'lit';
import { join } from 'lit/directives/join.js';
import { faviconUrl, domainOf } from '../lib/format.ts';
import { chipT } from '../lib/templates.ts';
import { BAD_STATUS, CAT_LABEL, canEdit } from '../derive/axes.ts';
import { styleGraphSelection } from './graph.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

const FIELD_LABELS: Record<string, [string, string?]> = {
  followers:['Followers'], rating:['Rating'], reviewCount:['Reviews'],
  address:['Address'], claimStatus:['Claim status'],
  impactMeanPosition:['Avg. Google position','IMPACT'],
  impactDurability:['Search durability (measured)','IMPACT'],
  aiCitations30d:['AI citations (30d)','AIQ'],
  aiCitationsTrend:['AI citations vs last month','AIQ'],
  employees:['Employees'], founded:['Founded'],
};
export function humanField(key: string): [string, string?] {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const s = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');
  return [s.charAt(0).toUpperCase() + s.slice(1)];
}
export function evidenceTier(a: NormalizedAsset): { label: string; why: string } {
  const f = a.fields || {};
  if (f.impactMeanPosition != null || f.aiCitations30d != null)
    return { label:'measured', why:'Backed by live IMPACT SERP and/or AIQ citation data.' };
  if ((a.corroboration||[]).length)
    return { label:'corroborated', why:'Cross-referenced by site markup, links, or Wikidata.' };
  if (a.verified)
    return { label:'page-fetched', why:'The URL was fetched during research, but not cross-referenced.' };
  return { label:'AI-assessed', why:'Inferred by the research agent — confirm before client use.' };
}

/* ── drawer (asset detail) ── */
export function openDrawer(state: AppPayloadState, id: string): void {
  const a = state.assets.find((x) => x.id === id); if (!a) return;
  state.selected = id;
  const ev = evidenceTier(a);
  const conflicted = (state.conflicts || []).find((c) => {
    const d = a.url ? domainOf(a.url) : null;
    if (d && (c.involvedUrls || []).some((u) => domainOf(u) === d)) return true;
    const plat = (a.platform || '').toLowerCase();
    return plat && plat.length > 2 && (c.description || '').toLowerCase().includes(plat);
  });
  const fieldRows = a.fields ? Object.entries(a.fields).map(([k, v]) => {
    const [lbl, src] = humanField(k);
    return html`<tr><td>${lbl}</td><td>${Array.isArray(v) ? v.join(', ') : String(v)}${src ? html` <span class="pill-meta">${src}</span>` : nothing}</td></tr>`;
  }) : [];
  const conns = state.edges.filter((e) => e.from === a.id || e.to === a.id).slice(0, 10).map((e) => {
    const other = e.from === a.id ? e.to : e.from;
    const oa = state.assets.find((x) => x.id === other);
    return html`<span style="font-family:var(--mono);font-size:10.5px;color:var(--muted)">${e.relation}</span> ${oa ? oa.title : other}`;
  });
  const ROLE_LABEL: Record<string, string> = { core:'CORE ENTITY', hub:'KEY PROPERTY', satellite:'SUPPORTING PROPERTY',
    profile:'PROFILE', listing:'LISTING', mention:'COVERAGE / MENTION' };
  const pf = a.pageFacts;
  const pageFactsLinked = pf ? (pf.linksToMap || []).map((id2) => { const x = state.assets.find((y) => y.id === id2); return x ? x.title : id2; }) : [];
  render(html`
    <div style="font-size:11px;font-weight:600;color:var(--muted)">${a.isCoreEntity ? '◆ CORE ENTITY' : (ROLE_LABEL[a.role] || (a.role || 'asset').toUpperCase())}</div>
    <div class="titlerow">${a.url ? html`<img src="${faviconUrl(a.url,64)}" alt="">` : nothing}<h3>${a.title || a.id}</h3></div>
    ${a.url ? html`<a class="url" href="${a.url}" target="_blank" rel="noopener">${a.url} ↗</a>` : html`<span class="url">no url captured</span>`}
    <div class="badges">
      ${chipT(CAT_LABEL[a.category] || a.category, a.category)}
      ${chipT(a.assetType,'')} ${a.platform ? chipT(a.platform,'') : nothing}
      ${chipT(a.status, BAD_STATUS.includes(a.status) ? 'warn' : a.status === 'active' ? 'ok' : '')}
      ${canEdit(a) ? chipT('client can edit','ok') : nothing}
      ${a.sentiment ? html`<span class="chip ${a.sentiment==='negative'?'bad':a.sentiment==='positive'?'ok':a.sentiment==='mixed'?'warn':''}" title="Sentiment is a research judgment, not a measured value.">${a.sentiment}</span>` : nothing}
    </div>
    ${conflicted ? html`<div class="reviewbox" style="border-color:var(--neg);color:var(--neg-strong)"><b>⚠ Referenced by an open conflict</b><br>${conflicted.description}</div>` : nothing}
    ${fieldRows.length ? html`<div style="font-size:11px;font-weight:600;color:var(--muted);margin:12px 0 6px">PROFILE FIELDS</div><table class="fieldstbl">${fieldRows}</table>` : nothing}
    <dl class="kv">
      <dt>Control</dt><dd>${a.controlLevel || '—'}</dd>
      <dt>Scope</dt><dd>${a.scope || '—'}${a.region && a.region !== a.scope ? ' · ' + a.region : ''}</dd>
      <dt>Evidence</dt><dd>${ev.label}${(a.corroboration||[]).length ? ' · ' + (a.corroboration||[]).join(', ') : ''}${ev.why ? html`<div style="font-size:11.5px;color:var(--muted);line-height:1.5;margin-top:2px">${ev.why}</div>` : nothing}</dd>
      ${pf ? html`<dt>Page (crawled)</dt><dd style="line-height:1.6">${pf.fetched ? `fetched · HTTP ${pf.status}` : (pf.reason || 'not fetched')}${pageFactsLinked.length ? html`<br><span style="font-size:11px;color:var(--muted)">links out to:</span> ${pageFactsLinked.slice(0,6).join(' · ')}${pageFactsLinked.length>6 ? ` +${pageFactsLinked.length-6}` : ''}` : nothing}${pf.canonical ? html`<br><span style="font-size:11px;color:var(--muted)">canonical:</span> ${pf.canonical}` : nothing}</dd>` : nothing}
      ${conns.length ? html`<dt>Connections</dt><dd style="line-height:1.7">${join(conns, html`<br>`)}</dd>` : nothing}
    </dl>
    ${a.notes ? html`<div class="notes">${a.notes}</div>` : nothing}
    ${a.needsReview ? html`<div class="reviewbox"><b>⚑ Needs review</b><br>${a.reviewReason || ''}</div>` : nothing}`, document.getElementById('drawerBody')!);
  document.getElementById('drawer')!.classList.add('open');
  styleGraphSelection(state);
  WS.renderDrawerExtras(a);
}
export function closeDrawer(state: AppPayloadState): void {
  document.getElementById('drawer')!.classList.remove('open');
  state.selected = null;
  styleGraphSelection(state);
}

export function initDrawerGlobalListeners(getState: () => AppPayloadState): void {
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(getState()); });
  document.addEventListener('click', (e) => {
    const dr = document.getElementById('drawer');
    if (!dr) return;
    const target = e.target as Element;
    // #map manages its own open/close on pointerup — pointer capture retargets
    // its clicks to the svg, so the map must be excluded here or the drawer
    // closes the instant a node opens it
    // .wsmenu/.modal-bk/.wstoast are workspace overlays appended to <body> —
    // interacting with them must never close the drawer that spawned them
    // (round-3 click-through: status change from the drawer ejected the user)
    if (dr.classList.contains('open') && !dr.contains(target) &&
        !target.closest('.mcell,.profcard,.serpitem,.slot,.dombar,.chancard,tr.click,.node,#map,.wsmenu,.modal-bk,.wstoast,.wsst,.wsmini')) closeDrawer(getState());
  });
}
